import React, { useEffect, useState } from 'react'
import LayoutFull from '../layouts/layoutFull/LayoutFull';
import { createFetchAsync } from "../helpers/fetch/fetchRequestAsync";
import { DEBUG } from "../settings/global";
import { handleStacksData } from "../helpers/data/get/stacks";
import { generateCategories, generateTagList } from "../helpers/data/get/clouds";
import { handlePlans } from "../helpers/data/get/plans";
import { store } from "../redux/store";
import { setPlans } from "../redux/actions/CommonDataActions";
import { getStackListJSONLD } from "../helpers/seo/stack";
import { JSONLD } from "../helpers/seo/seo";
import getPages from "../helpers/data/get/pages";
import SeoApiHandler from "../helpers/seo/SeoApiHandler";
import { convertEditorStateToHTML } from "../components/draftEditor/export/exports";
import { connect } from "react-redux";
import styles from "./applications.module.scss";
import Page, { Block, Content } from "../layouts/Content";
import Breadcrumb, { BreadcrumbLayer } from "../components/Breadcrumb";
import AppListSide from "../pagesComponents/applications/AppListSide";
import Button from "../components/ripple/Button";
import AppList from "../pagesComponents/applications/list/AppList";
import { processOrderChange, processQuery } from "../pagesComponents/applications/_heplers/appListProcessing";
import isEqual from "lodash.isequal";
import { useRouter } from "next/router";
import { immutableArrayItemUpdate } from "../helpers/common";
import { cliError } from "../helpers/cli/colors";
import { NAV_STACK_LIST } from "../settings/navigation";
import { API_PLANS_INTERNAL, API_STACKS_RATING_GET_INTERNAL } from "../settings/endpoints/user";
import { API_CLOUDS_CURRENCIES_CATEGORIES_INTERNAL, API_STACK_LIST_INTERNAL } from "../settings/endpoints/app";

export async function getStaticProps() {

    let stacksData = await createFetchAsync({
        URL: API_STACK_LIST_INTERNAL,
    });
    let stacks = null;

    const ratingsData = await createFetchAsync({
        URL: API_STACKS_RATING_GET_INTERNAL,
    });

    let ratings = {};

    ratingsData.data.rating.map(item => {
        if (item.stack_code in ratings) {
            ratings[item.stack_code].push({score: item.score, count: item.count})
        } else {
            ratings[item.stack_code] = [{score: item.score, count: item.count}];
        }
    });

    if (stacksData.success && !!stacksData.data) {

        let handledStacks = handleStacksData(stacksData.data);
        let stacksMinimized = [];

        if (!DEBUG) handledStacks = handledStacks.filter(app => !app.accessibility.hidden);

        handledStacks = handledStacks.sort((a, b) => {
            if (b.popularity - a.popularity === 0) {
                if (a.name > b.name) return 1;
                if (a.name < b.name) return -1;
                return 0
            }
            return b.popularity - a.popularity
        });

        handledStacks.map((app) => {

            if (!DEBUG && !!Object.keys(app._description?.entityMap || {})?.length) {
                cliError(`ONLY PLAIN TEXT IS ALLOWED! [${app.name} short description]. Please fix errors in admin interface.`);
                throw (`ONLY PLAIN TEXT IS ALLOWED! [${app.name} short description]. Please fix errors in admin interface.`);
            }

            stacksMinimized.push({
                id: app._id,
                code: app.code,
                name: app.name,
                version: app.version,
                category: app.category,
                popularity: app.popularity,
                updated: app._updated,
                accessibility: app.accessibility,
                requiredPlan: app.plan,
                icon: app.icon,
                _description:
                    !!app?._description?.blocks?.some?.(b => !!b.text)
                        ? convertEditorStateToHTML(app._description, {})
                        : null
                ,
                features: {
                    title: 'Features',
                    data: app.features?.map?.(feature => ({name: feature.name})) || [],
                    type: 'features'
                },
                services: {
                    title: 'Services',
                    data: app.services?.map?.(service => ({name: service.name})) || [],
                    type: 'services'
                },
                apps: {
                    title: 'Package includes',
                    data: app.apps?.length > 1 ? app.apps?.map?.(_app => ({name: _app.name})) : [],
                    type: 'apps'
                }
            })
        })

        stacks = stacksMinimized
    }

    let plans = await createFetchAsync({
        URL: API_PLANS_INTERNAL,
    });

    let handledPlans = handlePlans(plans.data.plans);

    let cloudsCurrenciesCategories = await createFetchAsync({
        URL: API_CLOUDS_CURRENCIES_CATEGORIES_INTERNAL,
    });

    let clouds = [];

    if (cloudsCurrenciesCategories.success && !!cloudsCurrenciesCategories.data) {

        clouds = cloudsCurrenciesCategories.data['cloud'];

        clouds = !DEBUG ? clouds.filter(cloud => !!cloud['impl'] && !cloud.lock) : clouds;

        clouds = clouds.map(c => {
            let newCloud = c;
            delete newCloud.description
            delete newCloud.help
            return newCloud
        })

        clouds = clouds.sort((a, b) => ('' + a.name).localeCompare(b.name));

        if (!!DEBUG) {
            clouds = clouds.sort((a, b) => {
                if (!!a.impl && !b.impl) return -1;
                if (!a.impl && !!b.impl) return 1;
                return 0
            });
            clouds = clouds.sort((a, b) => {
                if (!a.lock && b.lock) return -1;
                if (a.lock && !b.lock) return 1;
                return 0
            });
        }
    }

    const categories = generateCategories(cloudsCurrenciesCategories.data);
    const tags = [
        ...[{title: 'Free', active: false}],
        ...[{title: 'New', active: false}],
        ...generateTagList(categories)
            .filter(tag => tag.toLowerCase() !== 'new')
            .map(t => ({title: t, active: false}))
            .sort((a, b) => ('' + a.title).localeCompare(b.title)),
    ];

    return {
        props: {
            stacks,
            clouds,
            tags,
            plans: handledPlans,
            stackListJSONLD: getStackListJSONLD(handleStacksData(stacksData.data), ratings, categories),
            seo: await getPages(NAV_STACK_LIST),
        }
    };
}

function Apps({stacks, tags, plans, stackListJSONLD, seo, width}) {

    useEffect(() => {
        const storedPlans = store.getState().commonData.plans;
        if (!storedPlans?.length) store.dispatch(setPlans(plans));
    }, []);

    const router = useRouter();

    const [search, setSearch] = useState('');
    const [orderedAppData, setOrderedAppData] = useState(null);
    const [loading, setLoading] = useState(false);

    const defaultOrder = {
        criterion: 'popularity',
        descendant: true,
        tagsCriterion: 'category',
        type: 'sort',
        tags,
    };

    const [order, setOrder] = useState(defaultOrder);
    const _setOrder = keyValueObject => setOrder({...order, ...keyValueObject});

    useEffect(() => {
        setOrderedAppData(
            (!isEqual(order, defaultOrder) || !!search.length)
                ? processOrderChange(stacks, order, search)
                : null
        );
        if (loading) setLoading(false);
    }, [order, order.tags, search]);

    useEffect(() => {
        if (!!Object.keys(router.query || {})?.length) {
            _setOrder(processQuery(router.query, order));
            router.replace(NAV_STACK_LIST);
        }
    }, [router.query]);

    return (
        <LayoutFull>
            <SeoApiHandler data={seo}/>
            <JSONLD data={stackListJSONLD}/>
            <Page className={styles.apps}>
                <Block>
                    <Breadcrumb>
                        <BreadcrumbLayer link='/' name='Home'/>
                        <BreadcrumbLayer name='Applications'/>
                    </Breadcrumb>
                </Block>
                <Block>
                    <Content className={styles.apps__container} noSection>
                        <h1 className={styles.apps__container__title}>Applications & packages</h1>
                        <div className={styles.apps__container__content}>
                            <div className={styles.apps__container__content__side}>
                                <AppListSide
                                    search={search}
                                    width={width}
                                    setSearch={setSearch}
                                    order={order}
                                    setOrder={_setOrder}
                                    setLoading={setLoading}
                                />
                            </div>
                            <div className={styles.apps__container__content__main} id='appList-apps-container'>
                                {
                                    order.tags.some(t => !!t.active) &&
                                    <div className={styles.chips}>
                                        {
                                            order.tags.map((tag, tagIndex) => {
                                                if (!tag.active) return null;
                                                return (
                                                    <Button
                                                        title={tag.title}
                                                        iconRight='fas fa-times-circle'
                                                        onClick={() => {
                                                            const tags = immutableArrayItemUpdate(
                                                                order.tags,
                                                                tag,
                                                                {active: !tag.active}
                                                            );
                                                            _setOrder({tags})
                                                        }}
                                                        className={styles.chips__chip}
                                                        visuals={{
                                                            colorBasic: ['#e5e5e5', '#282828'],
                                                            colorFont: ['var(--colorFontCommon)'],
                                                            colorRippleTint: ['var(--colorFontLight)'],
                                                            borderRadius: '1.6rem',
                                                            height: '3.2rem',
                                                        }}
                                                        key={tagIndex}
                                                    />
                                                )
                                            })
                                        }
                                    </div>
                                }
                                <AppList
                                    apps={orderedAppData || stacks}
                                    loading={loading}
                                    type={order.type}
                                    plans={plans}
                                />
                            </div>
                        </div>
                    </Content>
                </Block>
            </Page>
        </LayoutFull>
    );
}

const mapStateToProps = store => ({
    width: store.UI.layoutDimensions.width,
});

export default connect(mapStateToProps)(Apps);